import {
    useMemo,
    useState,
    type ReactNode,
} from 'react'

import {
    useTranslation,
} from 'react-i18next'

import {
    DropZone as AriaDropZone,
    FileTrigger,
} from 'react-aria-components'

import {
    FileText,
    UploadCloud,
    X,
} from 'lucide-react'

import { Button } from './button'
import { cn } from '@/shared/lib/cn'

export interface RejectedFile {
    file: File
    reason: string
}

export interface FileDropZoneProps {
    label: ReactNode

    /**
     * Accessibility override when label is not a string.
     */
    ariaLabel?: string

    description?: ReactNode
    errorMessage?: ReactNode

    /**
     * Optional overrides for the default translated text.
     */
    instructions?: ReactNode
    browseLabel?: string

    getRemoveFileLabel?: (
        file: File,
    ) => string

    acceptedFileTypes?: readonly string[]
    allowsMultiple?: boolean

    maxFileSizeBytes?: number
    maxFiles?: number

    files?: readonly File[]
    defaultFiles?: readonly File[]

    onFilesChange?: (
        files: readonly File[],
    ) => void

    onFilesRejected?: (
        rejectedFiles: readonly RejectedFile[],
    ) => void

    isDisabled?: boolean
    isInvalid?: boolean

    className?: string
}

function fileKey(file: File): string {
    return [
        file.name,
        file.size,
        file.lastModified,
    ].join('-')
}

function formatFileSize(
    bytes: number,
    numberFormatter: Intl.NumberFormat,
): string {
    if (bytes < 1024) {
        return `${numberFormatter.format(bytes)} B`
    }

    if (bytes < 1024 * 1024) {
        return `${numberFormatter.format(
            bytes / 1024,
        )} KB`
    }

    return `${numberFormatter.format(
        bytes / (1024 * 1024),
    )} MB`
}

function matchesAcceptedType(
    file: File,
    acceptedTypes: readonly string[],
): boolean {
    if (acceptedTypes.length === 0) {
        return true
    }

    const lowerName =
        file.name.toLowerCase()

    return acceptedTypes.some(
        (acceptedType) => {
            const normalized =
                acceptedType.toLowerCase()

            if (normalized.startsWith('.')) {
                return lowerName.endsWith(
                    normalized,
                )
            }

            if (normalized.endsWith('/*')) {
                const prefix =
                    normalized.slice(0, -1)

                return file.type
                    .toLowerCase()
                    .startsWith(prefix)
            }

            return (
                file.type.toLowerCase() ===
                normalized
            )
        },
    )
}

export function FileDropZone({
    label,
    ariaLabel,
    description,
    errorMessage,
    instructions,
    browseLabel,
    getRemoveFileLabel,
    acceptedFileTypes = [],
    allowsMultiple = true,
    maxFileSizeBytes,
    maxFiles,
    files,
    defaultFiles = [],
    onFilesChange,
    onFilesRejected,
    isDisabled = false,
    isInvalid = Boolean(errorMessage),
    className,
}: FileDropZoneProps) {
    const {
        t,
        i18n,
    } = useTranslation()

    const [
        internalFiles,
        setInternalFiles,
    ] = useState<readonly File[]>(
        defaultFiles,
    )

    const selectedFiles =
        files ?? internalFiles

    const currentLocale =
        i18n.resolvedLanguage ??
        i18n.language

    const numberFormatter =
        useMemo(
            () =>
                new Intl.NumberFormat(
                    currentLocale,
                    {
                        maximumFractionDigits: 1,
                    },
                ),
            [currentLocale],
        )

    const resolvedAriaLabel =
        ariaLabel ??
        (
            typeof label === 'string'
                ? label
                : t(
                    'ui.dropZone.defaultLabel',
                )
        )

    const resolvedInstructions =
        instructions ??
        t('ui.dropZone.instructions')

    const resolvedBrowseLabel =
        browseLabel ??
        t('ui.file.browse')

    function resolveRemoveFileLabel(
        file: File,
    ): string {
        return (
            getRemoveFileLabel?.(file) ??
            t('ui.file.remove', {
                fileName: file.name,
            })
        )
    }

    function updateFiles(
        nextFiles: readonly File[],
    ): void {
        if (files === undefined) {
            setInternalFiles(nextFiles)
        }

        onFilesChange?.(nextFiles)
    }

    function validateFiles(
        incomingFiles: readonly File[],
    ): {
        accepted: File[]
        rejected: RejectedFile[]
    } {
        const accepted: File[] = []
        const rejected: RejectedFile[] = []

        for (const file of incomingFiles) {
            if (
                !matchesAcceptedType(
                    file,
                    acceptedFileTypes,
                )
            ) {
                rejected.push({
                    file,
                    reason: t(
                        'validation.invalidFileType',
                    ),
                })

                continue
            }

            if (
                maxFileSizeBytes !== undefined &&
                file.size > maxFileSizeBytes
            ) {
                rejected.push({
                    file,
                    reason: t(
                        'validation.fileTooLargeWithLimit',
                        {
                            size: formatFileSize(
                                maxFileSizeBytes,
                                numberFormatter,
                            ),
                        },
                    ),
                })

                continue
            }

            accepted.push(file)
        }

        return {
            accepted,
            rejected,
        }
    }

    function addFiles(
        incomingFiles: readonly File[],
    ): void {
        const validation =
            validateFiles(incomingFiles)

        let nextFiles = allowsMultiple
            ? [
                ...selectedFiles,
                ...validation.accepted,
            ]
            : validation.accepted.slice(0, 1)

        const uniqueFiles = new Map<
            string,
            File
        >()

        for (const file of nextFiles) {
            uniqueFiles.set(
                fileKey(file),
                file,
            )
        }

        nextFiles = Array.from(
            uniqueFiles.values(),
        )

        const rejected = [
            ...validation.rejected,
        ]

        if (
            maxFiles !== undefined &&
            nextFiles.length > maxFiles
        ) {
            const overflow =
                nextFiles.slice(maxFiles)

            for (const file of overflow) {
                rejected.push({
                    file,
                    reason: t(
                        'validation.maximumFiles',
                        {
                            count: maxFiles,
                        },
                    ),
                })
            }

            nextFiles =
                nextFiles.slice(0, maxFiles)
        }

        updateFiles(nextFiles)

        if (rejected.length > 0) {
            onFilesRejected?.(rejected)
        }
    }

    function removeFile(
        fileToRemove: File,
    ): void {
        updateFiles(
            selectedFiles.filter(
                (file) =>
                    fileKey(file) !==
                    fileKey(fileToRemove),
            ),
        )
    }

    return (
        <div
            className={cn(
                'flex flex-col gap-1.5',
                className,
            )}
        >
            <AriaDropZone
                aria-label={resolvedAriaLabel}
                isDisabled={isDisabled}
                getDropOperation={(types) => {
                    if (
                        acceptedFileTypes.length === 0
                    ) {
                        return 'copy'
                    }

                    const matchesMimeType =
                        acceptedFileTypes.some(
                            (acceptedType) => {
                                if (
                                    acceptedType.startsWith(
                                        '.',
                                    )
                                ) {
                                    /*
                                     * Drag data normally exposes MIME
                                     * types rather than filenames.
                                     * The exact extension is validated
                                     * after the file is received.
                                     */
                                    return true
                                }

                                if (
                                    acceptedType.endsWith(
                                        '/*',
                                    )
                                ) {
                                    const prefix =
                                        acceptedType.slice(
                                            0,
                                            -1,
                                        )

                                    return Array.from(
                                        types as unknown as Iterable<string>,
                                    ).some((type) =>
                                        type.startsWith(prefix),
                                    )
                                }

                                return types.has(
                                    acceptedType,
                                )
                            },
                        )

                    return matchesMimeType
                        ? 'copy'
                        : 'cancel'
                }}
                onDrop={async (event) => {
                    const droppedFiles: File[] = []

                    for (
                        const item of event.items
                    ) {
                        if (item.kind === 'file') {
                            droppedFiles.push(
                                await item.getFile(),
                            )
                        }
                    }

                    addFiles(droppedFiles)
                }}
                className={cn(
                    [
                        'flex min-h-52 flex-col',
                        'items-center justify-center',
                        'rounded-xl border-2',
                        'border-dashed p-6',
                        'text-center outline-none',
                        'transition-colors',
                        'data-focus-visible:ring-2',
                        'data-focus-visible:ring-(--color-primary)',
                        'data-drop-target:border-(--color-primary)',
                        'data-drop-target:bg-(--color-primary-muted)',
                        'data-disabled:opacity-60',
                    ],
                    isInvalid
                        ? 'border-(--color-danger)'
                        : 'border-(--color-border)',
                )}
            >
                <UploadCloud
                    aria-hidden="true"
                    className="h-10 w-10 text-(--color-primary)"
                />

                <span className="mt-4 text-sm font-semibold">
                    {label}
                </span>

                <p className="mt-1 max-w-md text-sm text-(--color-muted-foreground)">
                    {resolvedInstructions}
                </p>

                <FileTrigger
                    acceptedFileTypes={
                        acceptedFileTypes
                    }
                    allowsMultiple={allowsMultiple}
                    onSelect={(fileList) => {
                        addFiles(
                            fileList
                                ? Array.from(fileList)
                                : [],
                        )
                    }}
                >
                    <Button
                        type="button"
                        variant="secondary"
                        className="mt-4"
                        isDisabled={isDisabled}
                    >
                        {resolvedBrowseLabel}
                    </Button>
                </FileTrigger>
            </AriaDropZone>

            {description ? (
                <p className="text-xs leading-5 text-(--color-muted-foreground)">
                    {description}
                </p>
            ) : null}

            {errorMessage ? (
                <p
                    role="alert"
                    className="text-xs leading-5 text-(--color-danger)"
                >
                    {errorMessage}
                </p>
            ) : null}

            {selectedFiles.length > 0 ? (
                <ul className="mt-2 space-y-2">
                    {selectedFiles.map((file) => (
                        <li
                            key={fileKey(file)}
                            className="flex items-center gap-3 rounded-lg border border-(--color-border) bg-(--color-surface) px-4 py-3"
                        >
                            <FileText
                                aria-hidden="true"
                                className="h-5 w-5 shrink-0 text-(--color-muted-foreground)"
                            />

                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium">
                                    {file.name}
                                </div>

                                <div className="text-xs text-(--color-muted-foreground)">
                                    {formatFileSize(
                                        file.size,
                                        numberFormatter,
                                    )}
                                </div>
                            </div>

                            <Button
                                type="button"
                                variant="ghost"
                                size="small"
                                className="h-8 w-8 p-0"
                                aria-label={resolveRemoveFileLabel(
                                    file,
                                )}
                                onPress={() => {
                                    removeFile(file)
                                }}
                            >
                                <X
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                />
                            </Button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    )
}
